/*
 * Product image hover zoom.
 *
 * Magnifies the hovered gallery image inside a pane placed next to it: the
 * pane shows the part of the image sitting under the cursor, and a lens
 * rectangle marks that same part on the image itself, so moving the pointer
 * explores the picture the way a loupe would.
 *
 * The pane is laid beside the gallery whenever the viewport leaves enough room
 * on either side, and covers the image itself when it does not, which is what
 * happens with the full width gallery types and inside narrow windows.
 *
 * The magnification is the same for every product. What changes with the image
 * is the source the pane paints: the number of pixels needed depends on the
 * box object-fit paints the image into, which for a wide image cropped by
 * "cover" is far larger than the gallery element, so the width is measured at
 * hover time and asked of the image CDN then.
 *
 * Galleries opt in through the "Enable image zoom on hover" section setting:
 * without it the markup carries no zoom data attributes and the gallery keeps
 * its click-to-open-the-lightbox behaviour alone. The zoom is skipped where a
 * hover interaction cannot be reached, on touch devices and coarse pointers.
 */
(function () {
  if (window.ProductImageZoom) return;

  // The attribute sits on the modal opener, so hovering the toggle button that
  // covers the image still counts as hovering the media.
  const ROOT_SELECTOR = "[data-product-zoom]";
  // Galleries cloned into a modal or a quick view keep their plain behaviour,
  // and the no-script grid is a hidden duplicate of the gallery.
  const EXCLUDED_CONTEXTS =
    "quick-add-modal, product-modal, .product-media-modal, #quick-view, .product__media-grid-noscript";
  const IMAGE_SELECTOR = ".product__media img";
  const MEDIA_SELECTOR = ".product__media";
  const HOVER_QUERY = "(hover: hover) and (pointer: fine)";
  // Magnification applied to the image as displayed, not to its source file.
  const ZOOM_SCALE = 2.5;
  // Requested widths are snapped to this step so that resizing the window, or
  // hovering a gallery of same sized images, reuses one cached file.
  const SOURCE_STEP = 256;
  // Ceiling on what is pulled for a single hover. Reached only by very wide
  // images cropped into a tall gallery box, where the pane turns slightly
  // softer rather than the page pulling several megabytes.
  const MAX_SOURCE_WIDTH = 4096;
  const PANE_GAP = 16;
  const VIEWPORT_MARGIN = 16;
  // A pane narrower than this shows too little of the image to be read, so the
  // zoom covers the image instead of standing beside it.
  const MIN_PANE_WIDTH = 280;
  const ACTIVE_CLASS = "product-image-zoom-active";
  const PANE_VISIBLE_CLASS = "product-zoom-pane--visible";
  const PANE_INNER_CLASS = "product-zoom-pane--inner";
  const LENS_VISIBLE_CLASS = "product-zoom-lens--visible";

  const hoverQuery = window.matchMedia(HOVER_QUERY);

  let pane = null;
  let paneImage = null;
  let lens = null;
  let active = null;
  let pointer = null;
  let frame = null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const cssUrl = (source) => 'url("' + source.replace(/"/g, "%22") + '")';

  const build = () => {
    if (pane) return;

    pane = document.createElement("div");
    pane.className = "product-zoom-pane";
    pane.setAttribute("aria-hidden", "true");

    paneImage = document.createElement("div");
    paneImage.className = "product-zoom-pane__image";
    pane.appendChild(paneImage);

    lens = document.createElement("div");
    lens.className = "product-zoom-lens";
    lens.setAttribute("aria-hidden", "true");

    document.body.appendChild(pane);
    document.body.appendChild(lens);
  };

  const hide = () => {
    if (!pane) return;

    pane.classList.remove(PANE_VISIBLE_CLASS);
    lens.classList.remove(LENS_VISIBLE_CLASS);
  };

  // Window of the magnified content the pane shows, kept inside that content
  // unless it is smaller than the pane, in which case it is centred.
  const windowOffset = (center, size, total) => {
    if (total <= size) return (total - size) / 2;

    return clamp(center - size / 2, 0, total - size);
  };

  // Box the image really paints inside its element, which object-fit shrinks
  // or crops. The theme always centres it, so the leftover room is split.
  const getPaintedBox = (rect, ratio, fit) => {
    const isWide = rect.width / rect.height > ratio;

    if (fit === "contain" ? isWide : !isWide) {
      return { width: rect.height * ratio, height: rect.height };
    }

    return { width: rect.width, height: rect.width / ratio };
  };

  const getSourceWidth = (paintedWidth) => {
    const needed =
      Math.ceil((paintedWidth * ZOOM_SCALE) / SOURCE_STEP) * SOURCE_STEP;
    // Never ask for more than the merchant uploaded: the CDN would return the
    // original anyway, under a URL nothing else on the page shares.
    const ceiling = Math.min(active.natural.width, MAX_SOURCE_WIDTH);

    return Math.max(1, Math.min(needed, ceiling));
  };

  const getSourceUrl = (width) => {
    try {
      const url = new URL(active.source, window.location.href);
      url.searchParams.set("width", String(width));

      return url.href;
    } catch (error) {
      // An unparseable source would otherwise pull the full size original.
      return active.displayed || null;
    }
  };

  // The image already on screen is painted under the zoom source, so the pane
  // has something to show while the larger file is still on its way.
  const applySource = (width) => {
    const zoomed = getSourceUrl(width);
    const layers = [];

    if (zoomed) layers.push(zoomed);
    if (active.displayed && active.displayed !== zoomed) {
      layers.push(active.displayed);
    }

    paneImage.style.backgroundImage = layers.map(cssUrl).join(", ");
  };

  const getPaneBox = (rect) => {
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const roomRight = viewportWidth - rect.right - PANE_GAP - VIEWPORT_MARGIN;
    const roomLeft = rect.left - PANE_GAP - VIEWPORT_MARGIN;
    const maxWidth = Math.min(rect.width, viewportWidth - VIEWPORT_MARGIN * 2);

    let width;
    let left;

    if (roomRight >= MIN_PANE_WIDTH && roomRight >= roomLeft) {
      width = Math.min(maxWidth, roomRight);
      left = rect.right + PANE_GAP;
    } else if (roomLeft >= MIN_PANE_WIDTH) {
      width = Math.min(maxWidth, roomLeft);
      left = rect.left - PANE_GAP - width;
    } else {
      // No room on either side: the pane takes the place of the image.
      return {
        inner: true,
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    }

    const height = Math.min(rect.height, viewportHeight - VIEWPORT_MARGIN * 2);
    const maxTop = Math.max(
      VIEWPORT_MARGIN,
      viewportHeight - height - VIEWPORT_MARGIN
    );

    return {
      inner: false,
      left: left,
      top: clamp(rect.top, VIEWPORT_MARGIN, maxTop),
      width: width,
      height: height,
    };
  };

  const getGeometry = () => {
    if (!pointer) return null;

    const rect = active.image.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    // Scrolling can slide the image out from under a motionless pointer.
    if (
      pointer.x < rect.left ||
      pointer.x > rect.right ||
      pointer.y < rect.top ||
      pointer.y > rect.bottom
    )
      return null;

    const scale = ZOOM_SCALE;
    const ratio = active.natural.width / active.natural.height;
    const painted = getPaintedBox(rect, ratio, active.fit);
    const box = getPaneBox(rect);
    const originX = windowOffset(
      (pointer.x - rect.left) * scale,
      box.width,
      rect.width * scale
    );
    const originY = windowOffset(
      (pointer.y - rect.top) * scale,
      box.height,
      rect.height * scale
    );
    const paintedLeft = ((rect.width - painted.width) / 2) * scale;
    const paintedTop = ((rect.height - painted.height) / 2) * scale;
    const lensWidth = Math.min(box.width / scale, rect.width);
    const lensHeight = Math.min(box.height / scale, rect.height);

    return {
      inner: box.inner,
      paintedWidth: painted.width,
      pane: {
        left: box.left + window.scrollX,
        top: box.top + window.scrollY,
        width: box.width,
        height: box.height,
      },
      background: {
        size: painted.width * scale + "px " + painted.height * scale + "px",
        position: paintedLeft - originX + "px " + (paintedTop - originY) + "px",
      },
      lens: {
        left:
          rect.left +
          window.scrollX +
          clamp(originX / scale, 0, rect.width - lensWidth),
        top:
          rect.top +
          window.scrollY +
          clamp(originY / scale, 0, rect.height - lensHeight),
        width: lensWidth,
        height: lensHeight,
      },
    };
  };

  const render = () => {
    frame = null;

    if (!active) return;

    // The gallery markup is replaced on variant change. The next pointer move
    // picks the new markup up.
    if (!active.root.isConnected) {
      deactivate();
      return;
    }

    const geometry = getGeometry();
    if (!geometry) {
      hide();
      return;
    }

    const sourceWidth = getSourceWidth(geometry.paintedWidth);
    if (sourceWidth !== active.sourceWidth) {
      active.sourceWidth = sourceWidth;
      applySource(sourceWidth);
    }

    pane.style.width = geometry.pane.width + "px";
    pane.style.height = geometry.pane.height + "px";
    pane.style.transform =
      "translate(" + geometry.pane.left + "px, " + geometry.pane.top + "px)";
    pane.classList.toggle(PANE_INNER_CLASS, geometry.inner);
    paneImage.style.backgroundSize = geometry.background.size;
    paneImage.style.backgroundPosition = geometry.background.position;
    pane.classList.add(PANE_VISIBLE_CLASS);

    // Over the image the pane is its own lens, a second frame would only
    // repeat what it already shows.
    if (geometry.inner) {
      lens.classList.remove(LENS_VISIBLE_CLASS);
      return;
    }

    lens.style.width = geometry.lens.width + "px";
    lens.style.height = geometry.lens.height + "px";
    lens.style.transform =
      "translate(" + geometry.lens.left + "px, " + geometry.lens.top + "px)";
    lens.classList.add(LENS_VISIBLE_CLASS);
  };

  const schedule = () => {
    if (frame) return;

    frame = window.requestAnimationFrame(render);
  };

  // Leaving the window through one of its edges lands the pointer on nothing,
  // so no other element is there to end the zoom.
  const onPointerOut = (event) => {
    if (!event.relatedTarget) deactivate();
  };

  function deactivate() {
    if (!active) return;

    active = null;
    pointer = null;

    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }

    hide();
    document.removeEventListener("pointerout", onPointerOut);
    document.removeEventListener("pointerdown", deactivate);
    window.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    document.body.classList.remove(ACTIVE_CLASS);
  }

  const activate = (root) => {
    const image = root.querySelector(IMAGE_SELECTOR);
    if (!image) return false;

    const displayed = image.currentSrc || image.src;
    const source = root.dataset.zoomSrc || displayed;
    if (!source) return false;

    const width = Number(root.dataset.zoomWidth) || image.naturalWidth;
    const height = Number(root.dataset.zoomHeight) || image.naturalHeight;
    if (!width || !height) return false;

    build();

    active = {
      root: root,
      image: image,
      source: source,
      displayed: displayed,
      sourceWidth: 0,
      natural: { width: width, height: height },
      fit: window.getComputedStyle(image).objectFit,
    };

    const media = image.closest(MEDIA_SELECTOR);
    pane.style.backgroundColor = media
      ? window.getComputedStyle(media).backgroundColor
      : "";

    document.addEventListener("pointerout", onPointerOut);
    // A click opens the lightbox, which takes the pane's place.
    document.addEventListener("pointerdown", deactivate);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.body.classList.add(ACTIVE_CLASS);

    return true;
  };

  const track = (event) => {
    if (!hoverQuery.matches) return;

    const target = event.target;
    const root =
      target && target.closest ? target.closest(ROOT_SELECTOR) : null;

    // Anything else under the pointer, an opening modal included, ends the zoom.
    if (!root || root.closest(EXCLUDED_CONTEXTS)) {
      deactivate();
      return;
    }

    if (!active || active.root !== root) {
      deactivate();
      if (!activate(root)) return;
    } else if (!active.image.isConnected) {
      // After a variant change the theme detaches and re-inserts the media
      // elements; the modal opener survives, the image node does not have to.
      const image = root.querySelector(IMAGE_SELECTOR);
      if (!image) {
        deactivate();
        return;
      }

      active.image = image;
      active.displayed = image.currentSrc || image.src;
    }

    pointer = { x: event.clientX, y: event.clientY };
    schedule();
  };

  const onHoverChange = () => {
    if (!hoverQuery.matches) deactivate();
  };

  // Entering the media is the common case, but the gallery is also rebuilt
  // under a motionless cursor whenever a variant change makes the theme fetch
  // the section, and no pointerover follows that. Tracking moves as well is
  // what lets the zoom come back without leaving the image first.
  document.addEventListener("pointerover", track);
  document.addEventListener("pointermove", track, { passive: true });
  document.addEventListener("shopify:section:load", deactivate);
  document.addEventListener("shopify:section:unload", deactivate);
  document.addEventListener("shopify:section:select", deactivate);
  document.addEventListener("shopify:section:deselect", deactivate);

  hoverQuery.addEventListener("change", onHoverChange);

  window.ProductImageZoom = { close: deactivate };
})();
